"use client"

import { useState, useEffect, useRef } from "react"
import { Card, CardHeader, CardContent } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Bus, RefreshCw } from "lucide-react"
import axiosInstance from "../../utils/axiosInstance"
import maplibregl from "maplibre-gl"
import "maplibre-gl/dist/maplibre-gl.css"
import axios from "axios";
import { API_ENDPOINTS } from "@/constants/config";
import TRACKING_SERVICE_URL, { getWebSocketUrl } from "@/constants/tracking";
import { getToken } from "@/utils/authStorage";

const directionsControlStyle = `
.maplibregl-ctrl-directions {
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  font-family: inherit;
  font-size: 14px;
  padding: 8px;
  z-index: 1001;
}
.maplibregl-ctrl-directions input {
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 4px 8px;
  margin-bottom: 4px;
}
.maplibregl-ctrl-directions button {
  background: #4629eb;
  color: #fff;
  border: none;
  border-radius: 3px;
  padding: 4px 10px;
  cursor: pointer;
  margin-left: 4px;
}
.maplibregl-ctrl-directions button:hover {
  background: #2d1a8a;
}
`;

if (typeof document !== 'undefined' && !document.getElementById('directions-control-style')) {
  const style = document.createElement('style');
  style.id = 'directions-control-style';
  style.innerHTML = directionsControlStyle;
  document.head.appendChild(style);
}

const MobileTrackingApp = () => {
  const token = getToken();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [error, setError] = useState(null)
  const [wsConnected, setWsConnected] = useState(false)
  const [vehicles, setVehicles] = useState([])
  const [selectedVehicle, setSelectedVehicle] = useState("")
  const [loading, setLoading] = useState(true)
  const [schoolSettings, setSchoolSettings] = useState(null)
  
  
  const mapRef = useRef(null)
  const busMarkerRef = useRef(null)
  const wsRef = useRef(null)
  const mapLoadedRef = useRef(false) 
  const routePolylineRef = useRef(null)
  const busImgRef = useRef(null);
  const stopMarkersRef = useRef([]);
  const reconnectAttemptsRef = useRef(0);
  const initialLocationFetchedRef = useRef(false);
  const isFirstLocationUpdateRef = useRef(true);
  const shouldFollowBusRef = useRef(true);
  const lastMessageTimeRef = useRef(Date.now());
  const connectionCheckIntervalRef = useRef(null); 
  
  const INITIAL_ZOOM = 14
  const mapTilerKey = import.meta.env.VITE_MAPTILER_API_KEY;
  const [routeStops, setRouteStops] = useState([]);
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [studentStop, setStudentStop] = useState(null);
  const [studentRouteData, setStudentRouteData] = useState(null);
  const [availableShifts, setAvailableShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState("");
  const [vehicleLocationStatus, setVehicleLocationStatus] = useState(null);
  const [currentBusLocation, setCurrentBusLocation] = useState(null);
  const [etaData, setEtaData] = useState(null);
  const currentBusLocationRef = useRef(null);
  const etaIntervalRef = useRef(null);
  const lastEtaRequestRef = useRef(0);
  const [schoolLocation, setSchoolLocation] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const userLocationRequestedRef = useRef(false);

  const getInitialMapCenter = (stops, schoolLoc, busLoc, userLoc) => {
    if (busLoc?.lat && busLoc?.lng) {
      return [busLoc.lng, busLoc.lat];
    }

    if (stops && stops.length > 0) {
      const firstStop = stops[0];
      const lat = parseFloat(firstStop.latitude);
      const lng = parseFloat(firstStop.longitude);
      if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng)) {
        return [lng, lat];
      }
    }

    if (schoolLoc?.longitude && schoolLoc?.latitude) {
      const lat = parseFloat(schoolLoc.latitude);
      const lng = parseFloat(schoolLoc.longitude);
      if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng) && lat !== 0 && lng !== 0) {
        return [lng, lat];
      }
    }

    if (userLoc?.lat && userLoc?.lng) {
      return [userLoc.lng, userLoc.lat];
    }

    return null;
  };

  const requestUserLocation = () => {
    if (userLocationRequestedRef.current || !navigator.geolocation) {
      return;
    }

    userLocationRequestedRef.current = true;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      () => {
        // User denied or error - silently fail
      },
      { timeout: 5000, maximumAge: 60000 }
    );
  };

  useEffect(() => {
    if (mapRef.current) return;

    const tryInitialize = () => {
      const mapContainer = document.getElementById("map");
      if (!mapContainer) {
        return false;
      }

      // Ensure container has dimensions
      const rect = mapContainer.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        return false;
      }

      initializeMap();
      return true;
    };

    // Try immediately
    if (tryInitialize()) {
      return;
    }

    // Retry with delays
    const timeoutIds = [];
    const delays = [100, 300, 500, 1000, 2000];
    
    delays.forEach(delay => {
      const timeoutId = setTimeout(() => {
        if (!mapRef.current && tryInitialize()) {
          // Clear all remaining timeouts
          timeoutIds.forEach(id => clearTimeout(id));
        }
      }, delay);
      timeoutIds.push(timeoutId);
    });

    function initializeMap() {
      if (mapRef.current) return;
      
      const mapContainer = document.getElementById("map");
      if (!mapContainer) return;

      // Double-check dimensions
      const rect = mapContainer.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        // Force minimum dimensions
        mapContainer.style.minHeight = '400px';
        mapContainer.style.height = '100%';
      }
      
      requestUserLocation();
      
      const initialCenter = getInitialMapCenter([], null, null, null);
      
      try {
        mapRef.current = new maplibregl.Map({
          container: "map",
          style: `https://api.maptiler.com/maps/streets/style.json?key=${mapTilerKey}`,
          center: initialCenter || [0, 0],
          zoom: initialCenter ? INITIAL_ZOOM : 2,
        });

        mapRef.current.on('load', () => {
          mapLoadedRef.current = true;
          mapRef.current.addControl(new maplibregl.NavigationControl(), "top-right");
          // Trigger resize to ensure map renders properly
          setTimeout(() => {
            if (mapRef.current) {
              mapRef.current.resize();
            }
          }, 100);
        });

        mapRef.current.on('zoom', () => {
          if (busImgRef.current) {
            const zoom = mapRef.current.getZoom();
            const size = Math.max(32, Math.min(64, 32 + (zoom - 12) * 3));
            busImgRef.current.setAttribute('width', size);
            busImgRef.current.setAttribute('height', size);
          }
        });
      } catch (error) {
        console.error('Error initializing map:', error);
      }

      return () => {
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
          mapLoadedRef.current = false;
        }
      };
    }

    return () => {
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, []);

  // Additional effect to initialize map when container becomes available after loading
  useEffect(() => {
    if (mapRef.current || loading) {
      return;
    }
    
    // Don't initialize if we're still checking vehicle status for the first time
    if (selectedVehicle && vehicleLocationStatus === null) {
      return;
    }
    
    // Don't initialize map if vehicle is offline
    if (vehicleLocationStatus && !vehicleLocationStatus.isLocationSharingActive) {
      return;
    }

    const mapContainer = document.getElementById("map");
    if (mapContainer && !mapRef.current) {
      const rect = mapContainer.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        // Container is ready, trigger initialization
        setTimeout(() => {
          if (!mapRef.current) {
            const checkAndInit = () => {
              const container = document.getElementById("map");
              if (container && !mapRef.current) {
                const containerRect = container.getBoundingClientRect();
                if (containerRect.width > 0 && containerRect.height > 0) {
                  requestUserLocation();
                  const initialCenter = getInitialMapCenter([], null, null, null);
                  
                  try {
                    mapRef.current = new maplibregl.Map({
                      container: "map",
                      style: `https://api.maptiler.com/maps/streets/style.json?key=${mapTilerKey}`,
                      center: initialCenter || [0, 0],
                      zoom: initialCenter ? INITIAL_ZOOM : 2,
                      preserveDrawingBuffer: true, // Helps prevent blank map issues
                    });

                    mapRef.current.on('load', () => {
                      mapLoadedRef.current = true;
                      mapRef.current.addControl(new maplibregl.NavigationControl(), "top-right");
                      setTimeout(() => {
                        if (mapRef.current) {
                          mapRef.current.resize();
                        }
                      }, 100);
                    });

                    mapRef.current.on('zoom', () => {
                      if (busImgRef.current) {
                        const zoom = mapRef.current.getZoom();
                        const size = Math.max(32, Math.min(64, 32 + (zoom - 12) * 3));
                        busImgRef.current.setAttribute('width', size);
                        busImgRef.current.setAttribute('height', size);
                      }
                    });
                  } catch (error) {
                    console.error('Error initializing map:', error);
                  }
                }
              }
            };
            checkAndInit();
          }
        }, 100);
      }
    }
  }, [loading, selectedVehicle, vehicleLocationStatus]);

  useEffect(() => {
    if (!mapRef.current || !mapLoadedRef.current) return;
    
    const newCenter = getInitialMapCenter(
      routeStops, 
      schoolLocation, 
      currentBusLocationRef.current,
      userLocation
    );
    
    if (!newCenter) return;
    
    const currentCenter = mapRef.current.getCenter();
    const distance = Math.sqrt(
      Math.pow(currentCenter.lng - newCenter[0], 2) + 
      Math.pow(currentCenter.lat - newCenter[1], 2)
    );
    
    if (distance > 0.01) {
      mapRef.current.flyTo({
        center: newCenter,
        zoom: INITIAL_ZOOM,
        duration: 1000
      });
    }
  }, [routeStops, schoolLocation, userLocation]);

  const validateCoordinates = (lng, lat) => {
    if (isNaN(lat) || isNaN(lng) || !isFinite(lat) || !isFinite(lng)) {
      const fallback = getInitialMapCenter(
        routeStops, 
        schoolLocation, 
        currentBusLocationRef.current,
        userLocation
      );
      return fallback || [0, 0];
    }
    return [lng, lat];
  }

  const updateBusLocation = (busLocation) => {
    if (!mapRef.current || !mapLoadedRef.current || !busLocation.location) return
    const coordinates = busLocation.location.coordinates
    const validPosition = validateCoordinates(coordinates[0], coordinates[1])
    
    const location = {
      lat: validPosition[1],
      lng: validPosition[0]
    };
    setCurrentBusLocation(location);
    currentBusLocationRef.current = location;
    
    if (busMarkerRef.current) {
      // Just update the position, don't recreate the marker
      busMarkerRef.current.setLngLat(validPosition);
    } else if (mapRef.current.isStyleLoaded()) {
      // Only create marker if map is fully loaded
      const busIconElement = document.createElement("div");
      busIconElement.innerHTML = `
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g filter="url(#shadow)">
            <rect x="8" y="14" width="32" height="22" rx="4" fill="#FFB300" stroke="#fff" strokeWidth="2"/>
            <rect x="11" y="17" width="11" height="8" rx="1.5" fill="#4A90E2" opacity="0.8"/>
            <rect x="26" y="17" width="11" height="8" rx="1.5" fill="#4A90E2" opacity="0.8"/>
            <circle cx="16" cy="36" r="3" fill="#2C3E50" stroke="#fff" strokeWidth="1.5"/>
            <circle cx="32" cy="36" r="3" fill="#2C3E50" stroke="#fff" strokeWidth="1.5"/>
            <circle cx="12" cy="32" r="1.5" fill="#FFF176"/>
            <circle cx="36" cy="32" r="1.5" fill="#FFF176"/>
            <line x1="11" y1="14" x2="37" y2="14" stroke="#fff" strokeWidth="1.5" opacity="0.7"/>
          </g>
          <defs>
            <filter id="shadow" x="-2" y="-2" width="52" height="52">
              <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.3"/>
            </filter>
          </defs>
        </svg>
      `;
      busIconElement.style.display = "flex";
      busIconElement.style.alignItems = "center";
      busIconElement.style.justifyContent = "center";
      busIconElement.style.width = "48px";
      busIconElement.style.height = "48px";
      busIconElement.style.transform = "translate(-24px, -24px)";
      
      busMarkerRef.current = new maplibregl.Marker({ element: busIconElement })
        .setLngLat(validPosition)
        .addTo(mapRef.current)
      
      busImgRef.current = busIconElement.querySelector('svg');
    }
    
    if (isFirstLocationUpdateRef.current && mapRef.current && mapRef.current.isStyleLoaded()) {
      mapRef.current.flyTo({
        center: validPosition,
        zoom: 16,
        speed: 0.8,
        essential: true
      });
      isFirstLocationUpdateRef.current = false;
    }
    
    if (shouldFollowBusRef.current && mapRef.current && mapRef.current.isStyleLoaded()) {
      const lastFollowTime = mapRef.current._lastFollowTime || 0;
      const now = Date.now();
      if (now - lastFollowTime > 10000) {
        mapRef.current.easeTo({
          center: validPosition,
          duration: 800,
          easing: (t) => t * (2 - t)
        });
        mapRef.current._lastFollowTime = now;
      }
    }
  }

  const checkVehicleLocationStatus = async (vehicleId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        API_ENDPOINTS.vehicle(vehicleId),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === "success") {
        const vehicle = response.data.data.vehicle;
        setVehicleLocationStatus({
          isLocationSharingActive: vehicle.locationSharingActive || false,
          lastUpdate: vehicle.locationSharingStartTime,
          vehicleNumber: vehicle.vehicleNumber
        });
      }
    } catch (error) {
      setVehicleLocationStatus({
        isLocationSharingActive: false,
        lastUpdate: null,
        vehicleNumber: "Unknown"
      });
    }
  };

  
  // ============================================
  useEffect(() => {
    if (!selectedVehicle || !mapRef.current) {
      return;
    }

    // Don't connect if vehicle is offline
    if (vehicleLocationStatus && !vehicleLocationStatus.isLocationSharingActive) {
      return;
    }

    const connectWebSocket = () => {
      // Guard: without a token the server will reject the upgrade with 401
      if (!token) {
        console.warn("[MobileTrackingApp] WebSocket: no auth token found, live tracking disabled.");
        return;
      }

      // Clean up existing WebSocket connection first
      if (wsRef.current) {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.close(1000, "Switching vehicles");
        }
        wsRef.current = null;
      }

      // If we have a previous position, keep the map centered there during reconnection
      if (currentBusLocationRef.current && mapRef.current) {
        mapRef.current.setCenter([currentBusLocationRef.current.lng, currentBusLocationRef.current.lat]);
      }

      // Small delay to ensure old connection is closed
      setTimeout(() => {
        const ws = new WebSocket(getWebSocketUrl(token))

        let reconnectAttempts = 0;
        const maxReconnectAttempts = 5;
        const reconnectDelay = 3000;
        // Tracks whether onopen ever fired; used to detect upgrade-level auth rejection
        let wasOpened = false;

        wsRef.current = ws

        ws.onopen = () => {
          wasOpened = true;
          setWsConnected(true)
          setError(null)
          reconnectAttempts = 0;
          reconnectAttemptsRef.current = 0;
          lastMessageTimeRef.current = Date.now();

          ws.send(JSON.stringify({
            type: "subscribe",
            vehicleId: selectedVehicle,
          }));

          // Clear any existing connection check interval
          if (connectionCheckIntervalRef.current) {
            clearInterval(connectionCheckIntervalRef.current);
          }

          // Set up connection health check - if no messages for 60 seconds, reconnect
          connectionCheckIntervalRef.current = setInterval(() => {
            const timeSinceLastMessage = Date.now() - lastMessageTimeRef.current;
            if (timeSinceLastMessage > 60000 && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              wsRef.current.close(1006, 'Connection stale');
            }
          }, 30000);
        }

        ws.onmessage = (event) => {
          try {
            lastMessageTimeRef.current = Date.now();
            const data = JSON.parse(event.data)
            if (data.type === "locationUpdate" && data.busLocation) {
              if (data.busLocation.vehicle._id === selectedVehicle) {
                updateBusLocation(data.busLocation)
              }
            }
          } catch (error) {
            setError("Error processing WebSocket data. Attempting to reconnect...");
            setWsConnected(false)
          }
        }

        ws.onerror = () => {
          setError("Connection error occurred. Attempting to reconnect...");
          setWsConnected(false)
        }

        ws.onclose = (event) => {
          setWsConnected(false)

          // Upgrade rejected before connection opened — most likely an auth failure.
          // Stop reconnecting to avoid token-spam.
          if (!wasOpened && event.code === 1006) {
            console.warn("[MobileTrackingApp] WebSocket upgrade rejected — token may be expired. Please refresh.");
            setError("Tracking connection rejected. Please refresh the page.");
            return;
          }

          // Only reconnect if it wasn't a normal closure and we haven't exceeded max attempts
          if (event.code !== 1000 && reconnectAttempts < maxReconnectAttempts) {
            reconnectAttempts++;
            reconnectAttemptsRef.current = reconnectAttempts;
            setTimeout(connectWebSocket, reconnectDelay)
          } else if (reconnectAttempts >= maxReconnectAttempts) {
            // After max attempts, try one more time after a longer delay
            reconnectAttemptsRef.current = 0;
            setTimeout(() => {
              if (selectedVehicle && mapRef.current) {
                connectWebSocket();
              }
            }, 10000);
          }
        }
      }, 100);
    }

    connectWebSocket()

    if (!initialLocationFetchedRef.current && mapLoadedRef.current) {
      initialLocationFetchedRef.current = true;
      axios.get(`${TRACKING_SERVICE_URL}/api/vehicle-locations?vehicleId=${selectedVehicle}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
        .then(res => {
          if (res.data.status === 'success' && res.data.data && res.data.data.busLocations && res.data.data.busLocations.length > 0) {
            updateBusLocation(res.data.data.busLocations[0]);
          }
        })
        .catch(() => {});
    }

    return () => {
      // Clear connection check interval
      if (connectionCheckIntervalRef.current) {
        clearInterval(connectionCheckIntervalRef.current);
        connectionCheckIntervalRef.current = null;
      }
      
      if (wsRef.current) {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(
            JSON.stringify({
              type: "unsubscribe",
              busRouteId: selectedVehicle
            })
          )
        }
        wsRef.current.close(1000, "Component unmounting")
        wsRef.current = null
      }
      initialLocationFetchedRef.current = false;
      isFirstLocationUpdateRef.current = true;
      reconnectAttemptsRef.current = 0;
    }
  }, [selectedVehicle, vehicleLocationStatus, token]); 

  useEffect(() => {
    if (!mapLoadedRef.current || !mapRef.current) return;
    if (!routeCoordinates || routeCoordinates.length === 0) return;
    
    // Check if vehicle is offline - if so, remove route and return
    if (vehicleLocationStatus && !vehicleLocationStatus.isLocationSharingActive) {
      if (routePolylineRef.current && mapRef.current.getSource('route')) {
        try {
          mapRef.current.removeLayer('route-line');
          mapRef.current.removeSource('route');
        } catch (e) {
          // Silent fail
        }
        routePolylineRef.current = null;
      }
      return;
    }

    const addRoute = () => {
      // Only add route if it doesn't already exist
      if (routePolylineRef.current && mapRef.current.getSource('route')) {
        // Route already exists, just update the data
        try {
          mapRef.current.getSource('route').setData({
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: routeCoordinates,
            },
          });
        } catch (e) {
          // If update fails, recreate
          try {
            mapRef.current.removeLayer('route-line');
            mapRef.current.removeSource('route');
            routePolylineRef.current = null;
          } catch (e2) {
            // Silent fail
          }
        }
      }
      
      // Add route if it doesn't exist
      if (!routePolylineRef.current) {
        try {
          mapRef.current.addSource('route', {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: {
                type: 'LineString',
                coordinates: routeCoordinates,
              },
            },
          });
          mapRef.current.addLayer({
            id: 'route-line',
            type: 'line',
            source: 'route',
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': '#4629eb',
              'line-width': 4,
            },
          });
          routePolylineRef.current = true;
        } catch (e) {
          // Silent fail
        }
      }
    };

    if (mapRef.current.isStyleLoaded()) {
      addRoute();
    } else {
      mapRef.current.once('load', addRoute);
    }
  }, [routeCoordinates]);

  useEffect(() => {
    if (!mapLoadedRef.current || !mapRef.current) return;
    if (!routeStops.length) return;
    
    // Check if vehicle is offline - if so, remove markers and return
    if (vehicleLocationStatus && !vehicleLocationStatus.isLocationSharingActive) {
      stopMarkersRef.current.forEach(marker => {
        try {
          marker.remove();
        } catch (e) {
          // Silent fail
        }
      });
      stopMarkersRef.current = [];
      return;
    }

    const addStops = () => {
      // Only recreate markers if stops or student stop actually changed
      stopMarkersRef.current.forEach(marker => {
        try {
          marker.remove();
        } catch (e) {
          // Silent fail
        }
      });
      stopMarkersRef.current = [];
      
      routeStops.forEach((stop, index) => {
        const lat = parseFloat(stop.latitude);
        const lng = parseFloat(stop.longitude);
        const isCheckpoint = stop.isCheckpoint || false;
        const isStudentStop = studentStop && studentStop._id === stop._id;
        
        const markerSize = isCheckpoint ? 22 : 32;
        const fontSize = isCheckpoint ? 10 : 12;
        const borderWidth = isCheckpoint ? 2 : 3;
        
        let backgroundColor;
        if (isCheckpoint) {
          backgroundColor = '#ffa500';
        } else if (isStudentStop) {
          backgroundColor = '#ff6b35';
        } else if (index === 0) {
          backgroundColor = '#00bcd4';
        } else if (index === routeStops.length - 1) {
          backgroundColor = '#ff6b35';
        } else {
          backgroundColor = '#4629eb';
        }
        
        const el = document.createElement('div');
        el.className = 'stop-marker';
        el.innerHTML = `
          <div style="
            width: ${markerSize}px;
            height: ${markerSize}px;
            background: ${backgroundColor};
            border: ${borderWidth}px solid white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            color: white;
            font-size: ${fontSize}px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            cursor: pointer;
          ">
            ${index + 1}
          </div>
        `;
        
        let popupContent = `<strong>${stop.name}</strong><br/>Stop ${index + 1}`;
        if (isCheckpoint) {
          popupContent += `<br/><span style="color: #ffa500; font-weight: bold;">📍 Checkpoint</span>`;
        }
        if (isStudentStop && studentStop) {
          popupContent = `
            <div style="text-align: center;">
              <div style="color: #ff6b35; font-weight: bold; font-size: 16px; margin-bottom: 5px;">My Stop</div>
              <div style="font-weight: bold; margin-bottom: 3px;">${stop.name}</div>
              <div style="font-size: 12px; color: #666;">
                Stop ${index + 1} • ${studentStop.shiftType === 'pickup' ? 'Pickup' : 'Drop'} Stop
              </div>
            </div>
          `;
        }
        
        try {
          const marker = new maplibregl.Marker({ element: el })
            .setLngLat([lng, lat])
            .setPopup(
              new maplibregl.Popup({ offset: 25 })
                .setHTML(popupContent)
            )
            .addTo(mapRef.current);
          
          stopMarkersRef.current.push(marker);
        } catch (e) {
          // Silent fail
        }
      });
    };

    if (mapRef.current.isStyleLoaded()) {
      addStops();
    } else {
      mapRef.current.once('load', addStops);
    }
  }, [routeStops, studentStop]);

  // Fetch vehicles
  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        if (user && user.role === "student" && user._id) {
          const res = await axiosInstance.get(
            API_ENDPOINTS.vehicleRouteStudent(user._id),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (res.data.status === "success" && res.data.data.vehicleRoute && res.data.data.vehicleRoute.vehicle) {
            setVehicles([res.data.data.vehicleRoute.vehicle]);
            setSelectedVehicle(res.data.data.vehicleRoute.vehicle._id);
            setStudentRouteData(res.data.data.vehicleRoute);
            
            const studentId = user._id;
            let foundStop = null;
            
            if (res.data.data.vehicleRoute.shifts && res.data.data.vehicleRoute.shifts.length > 0) {
              for (const shift of res.data.data.vehicleRoute.shifts) {
                for (const stop of shift.stops) {
                  if (stop.students && stop.students.some(s => 
                    (typeof s === 'string' ? s : s._id) === studentId
                  )) {
                    foundStop = {
                      ...stop,
                      shiftType: shift.type,
                      shiftName: shift.name
                    };
                    break;
                  }
                }
                if (foundStop) break;
              }
            }
            
            setStudentStop(foundStop);
          } else {
            setVehicles([]);
            setSelectedVehicle("");
            setError("No vehicle assigned to you.");
          }
        } else {
          const response = await axiosInstance.get(API_ENDPOINTS.vehicles, {
            headers: { Authorization: `Bearer ${token}` },
          })
          if (response.data.status === "success") {
            setVehicles(response.data.data.vehicles)
            if (!selectedVehicle && response.data.data.vehicles.length > 0) {
              setSelectedVehicle(response.data.data.vehicles[0]._id)
            }
          } else {
            setError("No vehicles available")
          }
        }
      } catch (err) {
        setError(`Failed to load vehicles: ${err.response?.data?.message || err.message || "Unknown error"}`)
      } finally {
        setLoading(false)
      }
    }
    if (token) {
      fetchVehicles()
    } else {
      setError("Authentication token not found")
      setLoading(false)
    }
  }, [token, selectedVehicle])

  // Get schoolId
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (user && user.school) setSchoolId(user.school);
  }, []);

  // Fetch school settings and location
  useEffect(() => {
    const fetchSchoolSettings = async () => {
      try {
        const token = localStorage.getItem("token");
        const userStr = localStorage.getItem("user");
        if (!token || !userStr) return;
        
        const res = await axios.get(API_ENDPOINTS.schoolAbout, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSchoolSettings(res.data.data.settings);
        
        if (res.data.data.school?.location) {
          setSchoolLocation(res.data.data.school.location);
        }
      } catch (error) {
        // Silently fail - school settings are optional
      }
    };
    fetchSchoolSettings();
  }, []);

  // Vehicle status check
  useEffect(() => {
    if (selectedVehicle) {
      checkVehicleLocationStatus(selectedVehicle);
    }
  }, [selectedVehicle]);

  // Fetch route and stops
  useEffect(() => {
    const fetchRoute = async () => {
      if (!selectedVehicle || !schoolId) {
        return;
      }
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          API_ENDPOINTS.vehicleRoutesBySchool(schoolId),
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        const routes = res.data.data.vehicleRoutes || [];
        const route = routes.find(r => r.vehicle && r.vehicle._id === selectedVehicle);
        if (route && route.shifts && route.shifts.length > 0) {
          setAvailableShifts(route.shifts);
          
          if (!selectedShift && route.shifts.length > 0) {
            setSelectedShift(route.shifts[0]._id);
          }
          
          const currentShift = selectedShift ? 
            route.shifts.find(s => s._id === selectedShift) : 
            route.shifts[0];
            
          const stops = currentShift ? currentShift.stops || [] : [];
          setRouteStops(stops);
          
          let coords = stops.map(stop => [
            parseFloat(stop.longitude),
            parseFloat(stop.latitude)
          ]);
          
          if (coords.length >= 2) {
            try {
              const orsRes = await axios.post(
                `${TRACKING_SERVICE_URL}/api/vehicle-routes/ors-directions`,
                {
                  coordinates: coords,
                  profile: 'driving-car'
                },
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  }
                }
              );
              if (orsRes.data && orsRes.data.features && orsRes.data.features.length > 0) {
                const lineCoords = orsRes.data.features[0].geometry.coordinates;
                setRouteCoordinates(lineCoords);
              } else {
                setRouteCoordinates(coords);
              }
            } catch (orsErr) {
              setRouteCoordinates(coords);
            }
          } else {
            setRouteCoordinates(coords);
          }
        } else {
          setRouteStops([]);
          setRouteCoordinates([]);
        }
      } catch (err) {
        setRouteStops([]);
        setRouteCoordinates([]);
      }
    };
    fetchRoute();
  }, [selectedVehicle, schoolId, selectedShift]);

  // Periodic check for vehicle location status
  useEffect(() => {
    if (!selectedVehicle) return;

    const checkStatusInterval = setInterval(() => {
      checkVehicleLocationStatus(selectedVehicle);
    }, 30000);

    return () => clearInterval(checkStatusInterval);
  }, [selectedVehicle]);

  useEffect(() => {
    if (etaIntervalRef.current) {
      clearInterval(etaIntervalRef.current);
      etaIntervalRef.current = null;
    }

    if (!user || user.role !== "student" || !selectedVehicle || !studentStop) {
      setEtaData(null);
      lastEtaRequestRef.current = 0;
      return;
    }

    const getToken = () => localStorage.getItem("token");
    const currentVehicleId = selectedVehicle;
    const currentStop = studentStop;
    const currentStopLat = parseFloat(currentStop.latitude);
    const currentStopLng = parseFloat(currentStop.longitude);

    const fetchETA = async () => {
      const now = Date.now();
      const MIN_REQUEST_INTERVAL = 10000;
      
      if (now - lastEtaRequestRef.current < MIN_REQUEST_INTERVAL) {
        return;
      }

      if (!currentBusLocationRef.current || !currentVehicleId || !currentStop) {
        return;
      }

      lastEtaRequestRef.current = now;

      try {
        const token = getToken();
        const response = await axios.post(
          API_ENDPOINTS.vehicleRoutesCalculateEta,
          {
            vehicleId: currentVehicleId,
            targetLat: currentStopLat,
            targetLng: currentStopLng,
            targetType: 'stop'
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.status === 'success') {
          setEtaData(response.data.data);
        }
      } catch (error) {
        setEtaData(null);
      }
    };

    if (currentBusLocationRef.current) {
      fetchETA();
    }
    
    etaIntervalRef.current = setInterval(() => {
      if (currentBusLocationRef.current) {
        fetchETA();
      }
    }, 10000);

    return () => {
      if (etaIntervalRef.current) {
        clearInterval(etaIntervalRef.current);
        etaIntervalRef.current = null;
      }
    };
  }, [selectedVehicle, studentStop?._id, user?.role, user?._id]);

  const handleVehicleChange = (vehicleId) => {
    setSelectedVehicle(vehicleId)
    setSelectedShift("")
    setWsConnected(false)
    setError(null)
    setVehicleLocationStatus(null)
    isFirstLocationUpdateRef.current = true;
    initialLocationFetchedRef.current = false;
    currentBusLocationRef.current = null;
    setEtaData(null);
    lastEtaRequestRef.current = 0;
  }

  const handleShiftChange = (shiftId) => {
    setSelectedShift(shiftId)
  }

 
  
  if (schoolSettings && !schoolSettings.enableBusService?.enabled) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Bus className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">Bus Service Disabled</h3>
          <p className="text-gray-500">Bus tracking is currently not available for this school.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <RefreshCw className="mr-2 h-6 w-6 animate-spin" />
        <p>Loading vehicles...</p>
      </div>
    )
  }

  if (selectedVehicle && vehicleLocationStatus === null) {
    return (
      <div className="flex items-center justify-center p-6">
        <RefreshCw className="mr-2 h-6 w-6 animate-spin" />
        <p>Checking vehicle status...</p>
      </div>
    )
  }

  return (
    <Card className="w-full h-screen shadow-xl rounded-0 border-0 bg-gradient-to-br from-blue-50 to-white flex flex-col">
      <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-400 rounded-0 p-2 flex flex-row items-center justify-end gap-2 flex-shrink-0">
        {vehicleLocationStatus && vehicleLocationStatus.isLocationSharingActive && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-white/90 text-blue-700">
            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-1.5"></span>
            Online
          </span>
        )}
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold
          ${wsConnected ? "bg-white/90 text-green-700" : "bg-white/90 text-red-700"}`}
        >
          <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${wsConnected ? "bg-green-500" : "bg-red-500"}`}></span>
          {wsConnected ? "Connected" : "Reconnecting..."}
        </span>
      </CardHeader>
      <CardContent className="p-4 flex-1 flex flex-col overflow-hidden">
        {error ? (
          <Alert variant="destructive" className="mb-4 rounded-lg shadow bg-red-50 border-red-200 flex-shrink-0">
            <AlertDescription className="font-medium text-red-800 text-sm">{error}</AlertDescription>
          </Alert>
        ) : vehicles.length === 0 ? (
          <div className="p-6 text-center text-gray-500 text-sm flex-1 flex items-center justify-center">
            <p>No vehicles available. Please create a vehicle first.</p>
          </div>
        ) : (
          <div className="flex flex-col h-full relative">
            <div className="flex flex-col gap-3 mb-3 flex-shrink-0">
              <div className="flex-1">
                <Select value={selectedVehicle} onValueChange={handleVehicleChange} disabled={user && user.role === "student"}>
                  <SelectTrigger className="w-full h-10 text-sm border-blue-200 shadow-sm rounded-lg bg-white focus:border-blue-400">
                    <SelectValue placeholder="Select a vehicle" />
                  </SelectTrigger>
                  <SelectContent className="z-[9999] rounded-lg shadow-lg border-blue-100">
                    {vehicles.map((vehicle) => (
                      <SelectItem key={vehicle._id} value={vehicle._id} className="text-sm">
                        <span className="font-semibold text-blue-700">{vehicle.vehicleNumber}</span>
                        <span className="ml-2 text-xs text-gray-500">({vehicle.type})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {availableShifts.length > 1 && (
                <div className="flex-1">
                  <Select value={selectedShift} onValueChange={handleShiftChange}>
                    <SelectTrigger className="w-full h-10 text-sm border-blue-200 shadow-sm rounded-lg bg-white focus:border-blue-400">
                      <SelectValue placeholder="Select a shift" />
                    </SelectTrigger>
                    <SelectContent className="z-[9999] rounded-lg shadow-lg border-blue-100">
                      {availableShifts.map((shift) => (
                        <SelectItem key={shift._id} value={shift._id} className="text-sm">
                          <span className="font-semibold text-blue-700">{shift.name}</span>
                          <span className="ml-2 text-xs text-gray-500">({shift.type})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {/* ETA Display for Students */}
            {user && user.role === "student" && etaData && vehicleLocationStatus?.isLocationSharingActive && (
              <div className="mb-2 p-2 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg border-0 shadow-md flex-shrink-0">
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <p className="text-lg font-bold text-white leading-tight">
                      {etaData.estimatedTimeFormatted.replace(/:\d{2}(?=\s|$)/i, '')}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-blue-100">
                      {etaData.distanceKm} km
                    </p>
                  </div>
                </div>
              </div>
            )}
            {/* Show map or offline message based on vehicle status */}
            {vehicleLocationStatus && !vehicleLocationStatus.isLocationSharingActive ? (
              <div className="mt-2 relative z-10 rounded-0 overflow-hidden shadow-lg border border-blue-100 bg-gray-50 flex-1">
                <div className="w-full h-full rounded-0 border-0 flex items-center justify-center">
                  <div className="text-center px-4">
                    <Bus className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-gray-600 mb-1">Vehicle Offline</h3>
                    <p className="text-sm text-gray-500 mb-3">
                      {vehicleLocationStatus.vehicleNumber} is not sharing location.
                    </p>
                    <div className="inline-flex items-center px-3 py-1.5 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-1.5"></span>
                      Location Inactive
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-2 relative z-10 rounded-0 overflow-hidden shadow-lg border border-blue-100 flex-1" style={{ minHeight: '400px' }}>
                <div
                  id="map"
                  className="w-full h-full rounded-0 border-0"
                  style={{ position: "relative", zIndex: 10, minHeight: '400px', height: '100%' }}
                ></div>
              </div>
            )}
            {/* Legend - Sticky at bottom */}
            <div className="flex flex-wrap items-center gap-2 mt-0 justify-start p-2 bg-gray-50 rounded-0 shadow-sm border-t border-blue-100 text-xs flex-shrink-0 sticky bottom-0 z-20">
              <div className="flex items-center gap-1">
                <svg width="22" height="22" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="8" y="14" width="32" height="22" rx="4" fill="#FFB300" stroke="#fff" strokeWidth="2"/>
                  <rect x="11" y="17" width="11" height="8" rx="1.5" fill="#4A90E2" opacity="0.8"/>
                  <rect x="26" y="17" width="11" height="8" rx="1.5" fill="#4A90E2" opacity="0.8"/>
                  <circle cx="16" cy="36" r="3" fill="#2C3E50" stroke="#fff" strokeWidth="1.5"/>
                  <circle cx="32" cy="36" r="3" fill="#2C3E50" stroke="#fff" strokeWidth="1.5"/>
                </svg>
                <span className="text-gray-700">Bus</span>
              </div>
              <div className="flex items-center gap-1">
                <div style={{
                  width: '14px',
                  height: '14px',
                  background: '#00bcd4',
                  border: '1.5px solid white',
                  borderRadius: '50%',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                }}></div>
                <span className="text-gray-700">Start</span>
              </div>
              <div className="flex items-center gap-1">
                <div style={{
                  width: '14px',
                  height: '14px',
                  background: '#4629eb',
                  border: '1.5px solid white',
                  borderRadius: '50%',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                }}></div>
                <span className="text-gray-700">Stop</span>
              </div>
              <div className="flex items-center gap-1">
                <div style={{
                  width: '14px',
                  height: '14px',
                  background: '#ff6b35',
                  border: '1.5px solid white',
                  borderRadius: '50%',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                }}></div>
                <span className="text-gray-700">End</span>
              </div>
              <div className="flex items-center gap-1">
                <div style={{
                  width: '12px',
                  height: '12px',
                  background: '#ffa500',
                  border: '1.5px solid white',
                  borderRadius: '50%',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                }}></div>
                <span className="text-gray-700">Check</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default MobileTrackingApp
